/**
 * Hearts for one restaurant: the toggle, and a count that shows names on hover and the full list on click.
 */
import { useState } from "react";
import { formatDistanceToNow } from "date-fns";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { RuledList, RuledListItem } from "@/components/shared/ruled-list";
import { useAuth } from "@/providers/auth.provider";
import { cn } from "@/lib/utils";
import { Heart } from "lucide-react";

import { useRestaurantHearts, useToggleHeart } from "../hooks/use-hearts";
import type { HeartUser } from "../services/hearts.service";
import { HeartButton } from "./heart-button";

const PREVIEW_LIMIT = 6;

const displayName = (u: HeartUser, meId?: string) => (u.id === meId ? "You" : u.name);

export function RestaurantHeart({ restaurantId, className }: { restaurantId: string; className?: string }) {
  const { user } = useAuth();
  const { data } = useRestaurantHearts(restaurantId);
  const toggle = useToggleHeart(restaurantId);
  const [listOpen, setListOpen] = useState(false);

  const hearted = data?.heartedByMe ?? false;
  const count = data?.count ?? 0;
  const users = data?.users ?? [];
  const preview = users.slice(0, PREVIEW_LIMIT);
  const more = users.length - preview.length;

  return (
    <div className={cn("inline-flex items-center gap-1.5", className)} onClick={(e) => e.stopPropagation()}>
      <HeartButton hearted={hearted} disabled={!data} onToggle={() => toggle.mutate(!hearted)} />

      {count > 0 ? (
        <HoverCard openDelay={150} closeDelay={100}>
          <HoverCardTrigger asChild>
            <button
              type="button"
              onClick={() => setListOpen(true)}
              aria-label={`${count} ${count === 1 ? "heart" : "hearts"}, see who`}
              className="h-8 px-1.5 text-[13px] font-medium tabular-nums text-brand underline decoration-brand/40 underline-offset-[3px] hover:decoration-brand"
            >
              {count}
            </button>
          </HoverCardTrigger>
          <HoverCardContent
            align="start"
            sideOffset={6}
            className="w-auto min-w-44 max-w-64 rounded-sm border border-foreground bg-background p-0 text-[13px] shadow-[0_8px_24px_rgba(0,0,0,0.08)]"
          >
            <p className="flex items-center gap-1.5 border-b border-border px-3 py-2 text-[12px] text-muted-foreground">
              <Heart className="size-3 fill-brand text-brand" aria-hidden />
              Hearted by {count}
            </p>
            <ul className="flex flex-col py-1">
              {preview.map((u) => (
                <li key={u.id} className="px-3 py-1 font-medium">
                  {displayName(u, user?.id)}
                </li>
              ))}
              {more > 0 && (
                <li className="px-3 py-1 text-muted-foreground">
                  and {more} more
                </li>
              )}
            </ul>
          </HoverCardContent>
        </HoverCard>
      ) : (
        <span className="h-8 px-1.5 text-[13px] leading-8 tabular-nums text-muted-foreground">0</span>
      )}

      <Dialog open={listOpen} onOpenChange={setListOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-medium">
              Hearted by <span className="font-sans text-[13px] text-muted-foreground">{count}</span>
            </DialogTitle>
          </DialogHeader>
          <RuledList className="max-h-[60vh] overflow-y-auto">
            {users.map((u) => (
              <RuledListItem key={u.id} className="min-h-9 py-1.5">
                <span className="inline-flex items-center gap-2 font-medium">
                  <Heart className="size-3 fill-brand text-brand" aria-hidden />
                  {displayName(u, user?.id)}
                </span>
                <span className="text-[12.5px] text-muted-foreground">
                  {formatDistanceToNow(new Date(u.heartedAt), { addSuffix: true })}
                </span>
              </RuledListItem>
            ))}
          </RuledList>
        </DialogContent>
      </Dialog>
    </div>
  );
}
